import type { Turn } from '@/features/votes/chat-message';
import type {
  ChatHistory,
  DebugPayloadSkeleton,
} from '@/features/votes/message-votes';
import {
  groupIntoTurns,
  parseChatHistory,
} from '@/features/votes/parseChatHistory';

// What a step replay case would freeze for this vote, worked out client-side from the same
// data the backend builder uses so the dialog can show the target before anything is sent.
// The backend re-derives and re-validates all of it; this is a preview, not the contract.
export interface StepReplayTarget {
  turnNumber: number;
  stepIndex: number;
  stepCount: number;
  model: string | null;
  // the downvoted reply's visible text, so the curator can write defects against it
  recordedFinalResponse: string;
}

export type StepReplayIneligibility =
  | 'not_a_downvote'
  | 'history_not_shared'
  | 'not_turn_final_response'
  | 'payload_missing';

export type StepReplayTargetResult =
  | { target: StepReplayTarget; reason: null }
  | { target: null; reason: StepReplayIneligibility };

export function findStepReplayTarget(
  chatHistory: ChatHistory
): StepReplayTargetResult {
  if (chatHistory.vote_direction !== -1) {
    return { target: null, reason: 'not_a_downvote' };
  }
  if (!chatHistory.chat_history_is_shareable) {
    return { target: null, reason: 'history_not_shared' };
  }
  const votedIndex = chatHistory.vote_message_index;
  const turns = groupIntoTurns(parseChatHistory(chatHistory.chat_history));
  const votedTurn = turns.find(
    (turn) => turn.startIndex <= votedIndex && votedIndex <= turn.endIndex
  );
  // the builder freezes the reply the user finally saw, so the vote must sit on the turn's
  // last message; anything after it (another tool round, a retry) means it was not final
  if (!votedTurn || votedTurn.endIndex !== votedIndex) {
    return { target: null, reason: 'not_turn_final_response' };
  }
  const finalStep = findFinalStep(votedTurn, chatHistory.payloads);
  if (!finalStep) {
    return { target: null, reason: 'payload_missing' };
  }
  const votedMessage = votedTurn.messages[votedTurn.messages.length - 1];
  return {
    target: {
      turnNumber: votedTurn.turnNumber,
      stepIndex: finalStep.stepIndex,
      stepCount: finalStep.stepCount,
      model: finalStep.model,
      recordedFinalResponse: votedMessage.blocks
        .filter((block) => block.type === 'text')
        .map((block) => block.value)
        .join('\n'),
    },
    reason: null,
  };
}

function findFinalStep(
  turn: Turn,
  payloads: DebugPayloadSkeleton[]
): { stepIndex: number; stepCount: number; model: string | null } | null {
  // payload skeletons are keyed by the user message that opened the turn
  const turnMessageId = turn.messages.find(
    (message) => message.role === 'user'
  )?.id;
  if (!turnMessageId) return null;
  const stepped = payloads.filter(
    (payload): payload is SteppedPayload =>
      payload.message_id === turnMessageId && payload.step_index !== null
  );
  if (stepped.length === 0) return null;
  const finalStep = stepped.reduce((latest, payload) =>
    payload.step_index > latest.step_index ? payload : latest
  );
  return {
    stepIndex: finalStep.step_index,
    stepCount: new Set(stepped.map((payload) => payload.step_index)).size,
    model: finalStep.model,
  };
}

type SteppedPayload = DebugPayloadSkeleton & { step_index: number };

export const INELIGIBILITY_MESSAGE: Record<StepReplayIneligibility, string> = {
  not_a_downvote: 'Requires a downvote',
  history_not_shared: 'Requires a vote with shared chat history',
  not_turn_final_response:
    "Requires a downvote on a turn's final response (nothing may follow it in the turn)",
  payload_missing:
    'No stored request payload for the downvoted turn — debug rows expire after 30 idle days',
};
